import { z } from 'zod';
import { ResourceType, ResourceTypeSchema } from '../enumerations/ResourceType';
import { IdSchema, NameSchema } from './baseSchemas';
import { msObject } from './msObject';

export const PersistableInfoSchema = msObject({
    createdAt: z.string(),
    lastUpdatedAt: z.string().nullable(),
    createdBy: z.string().nullable(),
    lastUpdatedBy: z.string().nullable(),
});

export const PersistableResourceSchemaObject = {
    id: IdSchema,
    persistableInfo: PersistableInfoSchema,
    resourceType: ResourceTypeSchema,
};
export const PersistableResourceSchema = msObject(PersistableResourceSchemaObject);

export const ParentedPersistableResourceSchemaObject = {
    ...PersistableResourceSchemaObject,
    parentId: IdSchema,
};
export const ParentedPersistableResourceSchema = msObject(ParentedPersistableResourceSchemaObject);

export const NamedPersistableResourceSchemaObject = {
    ...PersistableResourceSchemaObject,
    name: NameSchema,
};
export const NamedPersistableResourceSchema = msObject(NamedPersistableResourceSchemaObject);

export const NamedParentedPersistableResourceSchemaObject = {
    ...ParentedPersistableResourceSchemaObject,
    ...NamedPersistableResourceSchemaObject,
};
export const NamedParentedPersistableResourceSchema = msObject(NamedParentedPersistableResourceSchemaObject);

export const getMultiParentedPersistableResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...ParentedPersistableResourceSchemaObject,
    parentRelation: parentRelationSchema,
});
export const createMultiParentedPersistableResourceSchema = <E extends string>(parentRelationSchema: z.ZodType<E, E>) =>
    msObject(getMultiParentedPersistableResourceSchemaObject(parentRelationSchema));

export const getNamedMultiParentedPersistableResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...NamedParentedPersistableResourceSchemaObject,
    ...getMultiParentedPersistableResourceSchemaObject(parentRelationSchema),
});
export const createNamedMultiParentedPersistableResourceSchema = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => msObject(getNamedMultiParentedPersistableResourceSchemaObject(parentRelationSchema));

export type PersistableInfo = z.infer<typeof PersistableInfoSchema>;

export type PersistableResource = z.infer<typeof PersistableResourceSchema>;

export type ParentedPersistableResource = z.infer<typeof ParentedPersistableResourceSchema>;

export type NamedPersistableResource = z.infer<typeof NamedPersistableResourceSchema>;

export type NamedParentedPersistableResource = z.infer<typeof NamedParentedPersistableResourceSchema>;

// type MultiParentedPersistableResourceType<E extends EnumLike> =
//     ReturnType<typeof createMultiParentedPersistableResourceSchema<E>>
// export type MultiParentedPersistableResource<E extends EnumLike> = z.infer<MultiParentedPersistableResourceType<E>>
//
// type NamedMultiParentedPersistableResourceType<E extends EnumLike> =
//     ReturnType<typeof createNamedMultiParentedPersistableResourceSchema<E>>
// export type NamedMultiParentedPersistableResource<E extends EnumLike> = z.infer<NamedMultiParentedPersistableResourceType<E>>

export type PersistableSchemaType<R extends PersistableResource> = z.Schema<
    R,
    Omit<R, 'resourceType'> & { resourceType?: ResourceType }
>;

export const getDefaultPersistableInfo = (): PersistableInfo => ({
    createdAt: '',
    lastUpdatedAt: null,
    createdBy: null,
    lastUpdatedBy: null,
});
export const defaultPersistableInfo: PersistableInfo = {
    createdAt: '',
    lastUpdatedAt: null,
    createdBy: null,
    lastUpdatedBy: null,
} as const;
export const defaultPersistableResource: PersistableResource = {
    id: '',
    persistableInfo: defaultPersistableInfo,
    resourceType: ResourceType.NONE,
} as const;
export const defaultParentedPersistableResource: ParentedPersistableResource = {
    ...defaultPersistableResource,
    parentId: '',
} as const;
export const defaultNamedPersistableResource: NamedPersistableResource = {
    ...defaultPersistableResource,
    name: '',
} as const;
export const defaultNamedParentedPersistableResource: NamedParentedPersistableResource = {
    ...defaultNamedPersistableResource,
    ...defaultParentedPersistableResource,
} as const;
